/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import Tooltip from '@mui/material/Tooltip';
import React, { useEffect } from 'react';

import { formatDate, formatDateTime } from '../utils';
import { CareEverywhereLogo } from './CareEverywhere/CareEverywhereLogo';
import styles from './FhirWidgetDateSelector.module.scss';
import { IObservationSimplified } from './fhir_types';
import { DiagnosticReportAndObservations } from './fhir_utils';

interface FhirWidgetDateSelectorProps {
  setHistoricalIndex?: (index: number) => void;
  historicalIndex: number;
  panelWithObservations: DiagnosticReportAndObservations[];
  showLoadingIndicator?: boolean;
  onUpdate?: () => void;
  lookbackTimeMonths?: number;
  expanded: boolean;
  setExpanded?: (expanded: boolean) => void;
  hasFullYear?: boolean;
}

export function FhirWidgetDateSelector({
  panelWithObservations,
  setHistoricalIndex,
  expanded,
  setExpanded,
  historicalIndex,
  showLoadingIndicator,
  onUpdate,
  lookbackTimeMonths,
  hasFullYear,
}: FhirWidgetDateSelectorProps) {
  const MAX = 3;
  let count = 0;
  let hasOlderThanLookbackTime = false;
  let hasMore = false;
  const dateFormat = hasFullYear ? 'MM/dd/yyyy' : 'M/d/yy';
  const dateTimeFormat = hasFullYear ? 'MM/dd/yyyy, HH:mm z' : 'M/d/yy, HH:mm z';
  useEffect(() => {
    // UpdateFhirPositions
    onUpdate?.();
  }, [expanded, onUpdate]);
  return (
    <div className={styles.dateList}>
      {panelWithObservations.map((o, i) => {
        if (o.hidePanelFromDateSelector) {
          return null;
        }
        const date =
          o.diagnosticReport.effectiveDateTime ||
          (o.diagnosticReport.result?.[0] as IObservationSimplified | undefined)?.effectiveDateTime;

        // Check if today - date in months is older than lookbackTimeMonths
        if (i > 0 && lookbackTimeMonths && lookbackTimeMonths > 0 && o.ageMonths && o.ageMonths > lookbackTimeMonths) {
          hasOlderThanLookbackTime = true;
          if (!expanded) {
            return null;
          }
        }
        if (count >= MAX) {
          hasMore = true;
          if (!expanded) {
            return null;
          }
        }
        count += 1;

        return (
          <Tooltip
            placement="left"
            enterDelay={500}
            title={
              <div>
                <div style={{ color: 'cyan' }}>{formatDateTime(date, dateTimeFormat)}</div>
                {o.diagnosticReport?.isCareEverywhere ? (
                  <div>
                    <CareEverywhereLogo />
                    &nbsp;From CareEverywhere
                  </div>
                ) : null}
                {o.diagnosticReport.code?.text ? <b>{o.diagnosticReport.code.text}</b> : null}
                {o.diagnosticReport.code?.coding?.length ? (
                  <div>
                    {o.diagnosticReport.code.coding
                      .map((c) => (c.display ? `${c.display} (${c.code})` : c.code || ''))
                      .join(', ')}
                  </div>
                ) : null}
                {o.diagnosticReport.result?.length ? (
                  <ul style={{ marginTop: '4px', paddingLeft: '16px' }}>
                    {o.diagnosticReport.result.map((r, ri) => {
                      const obs = r as IObservationSimplified;
                      return (
                        <li key={ri}>
                          {obs.code?.text ||
                            obs.code?.coding
                              ?.map((c) => (c.display ? `${c.display} (${c.code})` : c.code || ''))
                              .join(', ') ||
                            'Unknown observation'}
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </div>
            }
            key={i}
          >
            <div
              className={`${styles.date} ${i === historicalIndex ? styles.dateSelected : undefined}`}
              onClick={() => setHistoricalIndex?.(i)}
            >
              {o.diagnosticReport?.isCareEverywhere ? (
                <>
                  <CareEverywhereLogo />
                  &nbsp;
                </>
              ) : null}
              {formatDate(date, dateFormat)}
            </div>
          </Tooltip>
        );
      })}
      {showLoadingIndicator && (expanded || hasMore || hasOlderThanLookbackTime) && (
        <div className={styles.dateLoading}>██ ██ ███</div>
      )}
      {setExpanded && (hasOlderThanLookbackTime || hasMore) && (
        <div
          className={styles.dateToggle}
          onClick={() => setExpanded(!expanded)}
          title={expanded ? 'Show less...' : 'Show earlier results...'}
        >
          {expanded ? 'less...' : 'more...'}
        </div>
      )}
    </div>
  );
}
